import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Image as ImageIcon, Plus, Trash2, Edit3, Upload, Save, X, FileImage } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Tables } from '@/integrations/supabase/types';
type IndustryImage = Tables<'industry_images'>;
const INDUSTRIES = [{
  value: 'ecommerce',
  label: 'E-commerce'
}, {
  value: 'marketing',
  label: 'Marketing'
}, {
  value: 'creative-agencies',
  label: 'Creative Agencies'
}, {
  value: 'publishing',
  label: 'Publishing'
}, {
  value: 'real-estate',
  label: 'Real Estate'
}, {
  value: 'photography',
  label: 'Photography'
}, {
  value: 'gaming',
  label: 'Gaming'
}, {
  value: 'social-media',
  label: 'Social Media'
}];
const AdminIndustryImages = () => {
  const [images, setImages] = useState<IndustryImage[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState('ecommerce');
  const [loading, setLoading] = useState(true);
  const [editingImage, setEditingImage] = useState<IndustryImage | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMode, setUploadMode] = useState<'single' | 'multiple'>('single');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    display_order: 1
  });

  // Multiple upload state
  const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  useEffect(() => {
    fetchImages();
  }, [selectedIndustry]);
  const fetchImages = async () => {
    try {
      setLoading(true);
      const {
        data,
        error
      } = await supabase.from('industry_images').select('*').eq('industry', selectedIndustry).order('display_order', {
        ascending: true
      });
      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  };
  const handleSingleFileUpload = async (file: File) => {
    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `industry-${selectedIndustry}-${Date.now()}.${fileExt}`;
      const filePath = `industry-images/${fileName}`;
      console.log('Uploading single file:', filePath);
      const {
        error: uploadError,
        data
      } = await supabase.storage.from('user-images').upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }
      const {
        data: {
          publicUrl
        }
      } = supabase.storage.from('user-images').getPublicUrl(filePath);
      console.log('File uploaded successfully, public URL:', publicUrl);
      setFormData(prev => ({
        ...prev,
        image_url: publicUrl
      }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image: ' + (error as any)?.message || 'Unknown error');
    } finally {
      setUploading(false);
    }
  };
  const handleMultipleFilesSelect = (files: FileList) => {
    const fileArray = Array.from(files);
    setMultipleFiles(fileArray);

    // Create preview URLs
    const urls = fileArray.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };
  const uploadMultipleFiles = async () => {
    if (multipleFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }
    try {
      setUploading(true);
      console.log('Starting multiple file upload for', multipleFiles.length, 'files');
      const uploadPromises = multipleFiles.map(async (file, index) => {
        const fileExt = file.name.split('.').pop();
        const cleanFileName = file.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9-_\s]/g, '').trim();
        const fileName = `industry-${selectedIndustry}-${Date.now()}-${index}.${fileExt}`;
        const filePath = `industry-images/${fileName}`;
        console.log(`Uploading file ${index + 1}:`, filePath);
        const {
          error: uploadError
        } = await supabase.storage.from('user-images').upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
        if (uploadError) {
          console.error(`Upload error for file ${index + 1}:`, uploadError);
          throw uploadError;
        }
        const {
          data: {
            publicUrl
          }
        } = supabase.storage.from('user-images').getPublicUrl(filePath);

        // Generate clean title from filename
        const autoTitle = cleanFileName || `${INDUSTRIES.find(i => i.value === selectedIndustry)?.label || selectedIndustry} Image ${index + 1}`;
        return {
          industry: selectedIndustry,
          title: autoTitle,
          description: `${INDUSTRIES.find(i => i.value === selectedIndustry)?.label || selectedIndustry} industry example`,
          image_url: publicUrl,
          display_order: (images.length || 0) + index + 1
        };
      });
      console.log('Waiting for all uploads to complete...');
      const uploadedImages = await Promise.all(uploadPromises);

      // Insert all images at once
      console.log('Inserting images into database:', uploadedImages);
      const {
        error: insertError
      } = await supabase.from('industry_images').insert(uploadedImages);
      if (insertError) {
        console.error('Database insert error:', insertError);
        throw insertError;
      }
      toast.success(`Successfully uploaded ${multipleFiles.length} images`);
      setShowAddDialog(false);
      resetForm();
      fetchImages();
    } catch (error) {
      console.error('Error uploading multiple images:', error);
      toast.error('Failed to upload some images: ' + (error as any)?.message || 'Unknown error');
    } finally {
      setUploading(false);
    }
  };
  const handleAddSingleImage = async () => {
    try {
      if (!formData.title || !formData.image_url) {
        toast.error('Title and image are required');
        return;
      }
      console.log('Adding single image:', formData);
      const {
        error
      } = await supabase.from('industry_images').insert({
        industry: selectedIndustry,
        title: formData.title,
        description: formData.description,
        image_url: formData.image_url,
        display_order: formData.display_order
      });
      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }
      toast.success('Image added successfully');
      setShowAddDialog(false);
      resetForm();
      fetchImages();
    } catch (error) {
      console.error('Error adding image:', error);
      toast.error('Failed to add image: ' + (error as any)?.message || 'Unknown error');
    }
  };
  const handleUpdateImage = async () => {
    try {
      if (!editingImage || !formData.title || !formData.image_url) {
        toast.error('Title and image are required');
        return;
      }
      const {
        error
      } = await supabase.from('industry_images').update({
        title: formData.title,
        description: formData.description,
        image_url: formData.image_url,
        display_order: formData.display_order
      }).eq('id', editingImage.id);
      if (error) throw error;
      toast.success('Image updated successfully');
      setEditingImage(null);
      resetForm();
      fetchImages();
    } catch (error) {
      console.error('Error updating image:', error);
      toast.error('Failed to update image');
    }
  };
  const handleDeleteImage = async (id: string) => {
    try {
      const {
        error
      } = await supabase.from('industry_images').delete().eq('id', id);
      if (error) throw error;
      toast.success('Image deleted successfully');
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };
  const startEdit = (image: IndustryImage) => {
    setEditingImage(image);
    setFormData({
      title: image.title,
      description: image.description || '',
      image_url: image.image_url,
      display_order: image.display_order
    });
  };
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      image_url: '',
      display_order: 1
    });
    setMultipleFiles([]);
    setPreviewUrls([]);
    setUploadMode('single');
  };
  const removePreviewImage = (index: number) => {
    const newFiles = multipleFiles.filter((_, i) => i !== index);
    const newUrls = previewUrls.filter((_, i) => i !== index);
    setMultipleFiles(newFiles);
    setPreviewUrls(newUrls);
  };
  const selectedIndustryLabel = INDUSTRIES.find(i => i.value === selectedIndustry)?.label || selectedIndustry;
  return <div className="space-y-6">
      <Card className="bg-white/10 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <div className="flex items-center">
              <ImageIcon className="w-6 h-6 mr-2" />
              Industry Images Management
            </div>
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button onClick={() => {
                resetForm();
                setShowAddDialog(true);
              }} className="bg-gradient-to-r from-blue-600 to-purple-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Images
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-white/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add {selectedIndustryLabel} Images</DialogTitle>
                  <DialogDescription className="sr-only">Upload images for the {selectedIndustryLabel} industry</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Upload Mode Selection */}
                  <div>
                    <label className="text-sm text-gray-300 mb-2 block">Upload Mode</label>
                    <div className="flex gap-4">
                      <Button variant={uploadMode === 'single' ? 'default' : 'outline'} onClick={() => setUploadMode('single')} className="flex-1">
                        Single Image
                      </Button>
                      <Button variant={uploadMode === 'multiple' ? 'default' : 'outline'} onClick={() => setUploadMode('multiple')} className="flex-1 bg-[#000a00]/[0.01]">
                        Multiple Images
                      </Button>
                    </div>
                  </div>

                  {uploadMode === 'single' ?
                // Single Image Upload
                <>
                      <div>
                        <label className="text-sm text-gray-300 mb-2 block">Title</label>
                        <Input value={formData.title} onChange={e => setFormData(prev => ({
                      ...prev,
                      title: e.target.value
                    }))} className="bg-white/10 border-white/20 text-white" placeholder="Enter image title" />
                      </div>
                      
                      <div>
                        <label className="text-sm text-gray-300 mb-2 block">Description</label>
                        <Textarea value={formData.description} onChange={e => setFormData(prev => ({
                      ...prev,
                      description: e.target.value
                    }))} className="bg-white/10 border-white/20 text-white" placeholder="Enter image description" />
                      </div>

                      <div>
                        <label className="text-sm text-gray-300 mb-2 block">Image Upload</label>
                        <div className="space-y-2">
                          <input type="file" accept="image/*" onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleSingleFileUpload(file);
                      }} className="hidden" id="single-image-upload" />
                          <label htmlFor="single-image-upload" className="flex items-center justify-center w-full p-3 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/40 transition-colors">
                            <Upload className="w-4 h-4 mr-2" />
                            {uploading ? 'Uploading...' : 'Choose Image'}
                          </label>
                          {formData.image_url && <div className="mt-2">
                              <img 
                                src={formData.image_url} 
                                alt="Preview" 
                                className="w-full h-32 object-cover rounded" 
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEzMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEzMCIgZmlsbD0iIzM3NDE0OSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBQcmV2aWV3PC90ZXh0Pjwvc3ZnPg==';
                                }}
                              />
                            </div>}
                        </div>
                      </div>

                      <div>
                        <label className="text-sm text-gray-300 mb-2 block">Display Order</label>
                        <Input type="number" value={formData.display_order} onChange={e => setFormData(prev => ({
                      ...prev,
                      display_order: parseInt(e.target.value) || 1
                    }))} className="bg-white/10 border-white/20 text-white" min="1" />
                      </div>

                      <Button onClick={handleAddSingleImage} disabled={uploading || !formData.title || !formData.image_url} className="w-full bg-gradient-to-r from-blue-600 to-purple-600">
                        <Save className="w-4 h-4 mr-2" />
                        Add Image
                      </Button>
                    </> :
                // Multiple Images Upload
                <>
                      <div>
                        <label className="text-sm text-gray-300 mb-2 block">Multiple Images Upload</label>
                        <div className="space-y-2">
                          <input type="file" accept="image/*" multiple onChange={e => {
                        if (e.target.files) {
                          handleMultipleFilesSelect(e.target.files);
                        }
                      }} className="hidden" id="multiple-images-upload" />
                          <label htmlFor="multiple-images-upload" className="flex items-center justify-center w-full p-3 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/40 transition-colors">
                            <FileImage className="w-4 h-4 mr-2" />
                            Choose Multiple Images
                          </label>
                        </div>
                      </div>

                      {previewUrls.length > 0 && <div>
                          <label className="text-sm text-gray-300 mb-2 block">
                            Selected Images ({previewUrls.length})
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-60 overflow-y-auto">
                            {previewUrls.map((url, index) => <div key={index} className="relative">
                                <img src={url} alt={`Preview ${index + 1}`} className="w-full h-20 object-cover rounded" />
                                <button onClick={() => removePreviewImage(index)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">
                                  <X className="w-3 h-3" />
                                </button>
                                <div className="mt-1 text-xs text-gray-300 truncate">
                                  {multipleFiles[index]?.name}
                                </div>
                              </div>)}
                          </div>
                        </div>}

                      <Button onClick={uploadMultipleFiles} disabled={uploading || multipleFiles.length === 0} className="w-full bg-gradient-to-r from-green-600 to-blue-600">
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : `Upload ${multipleFiles.length} Images`}
                      </Button>
                    </>}
                </div>
              </DialogContent>
            </Dialog>
          </CardTitle>
          
          <div className="flex gap-4 mt-4">
            <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
              <SelectTrigger className="w-64 bg-white/10 border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map(industry => <SelectItem key={industry.value} value={industry.value}>
                    {industry.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        
        <CardContent>
          {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => <div key={i} className="bg-white/5 rounded-lg p-4 animate-pulse">
                  <div className="aspect-square bg-white/10 rounded mb-4"></div>
                  <div className="h-4 bg-white/10 rounded mb-2"></div>
                  <div className="h-3 bg-white/5 rounded"></div>
                </div>)}
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map(image => <div key={image.id} className="bg-white/5 rounded-lg overflow-hidden">
                  <div className="aspect-square bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center overflow-hidden">
                    <img 
                      src={image.image_url.startsWith('/') 
                        ? `${window.location.origin}${image.image_url}` 
                        : image.image_url
                      } 
                      alt={image.title} 
                      className="w-full h-full object-cover" 
                      onError={(e) => {
                        console.error('Failed to load image:', image.image_url);
                        e.currentTarget.style.display = 'none';
                        
                        // Create fallback using safe DOM manipulation (no innerHTML)
                        const fallbackDiv = document.createElement('div');
                        fallbackDiv.className = 'flex items-center justify-center w-full h-full text-blue-400';
                        
                        const container = document.createElement('div');
                        container.className = 'text-center';
                        
                        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                        svg.setAttribute('class', 'w-12 h-12 mx-auto mb-2');
                        svg.setAttribute('fill', 'none');
                        svg.setAttribute('stroke', 'currentColor');
                        svg.setAttribute('viewBox', '0 0 24 24');
                        
                        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                        path.setAttribute('stroke-linecap', 'round');
                        path.setAttribute('stroke-linejoin', 'round');
                        path.setAttribute('stroke-width', '2');
                        path.setAttribute('d', 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z');
                        
                        svg.appendChild(path);
                        container.appendChild(svg);
                        
                        const text = document.createElement('p');
                        text.className = 'text-xs';
                        text.textContent = 'Image failed to load';
                        container.appendChild(text);
                        
                        fallbackDiv.appendChild(container);
                        e.currentTarget.parentElement!.appendChild(fallbackDiv);
                      }}
                    />
                  </div>
                  
                  <div className="p-4">
                    <h4 className="text-sm font-medium text-white mb-1">{image.title}</h4>
                    <p className="text-xs text-gray-400 mb-3">{image.description}</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => startEdit(image)} className="bg-blue-600 hover:bg-blue-700 text-xs px-2 py-1">
                        <Edit3 className="w-3 h-3" />
                      </Button>
                      <Button size="sm" onClick={() => handleDeleteImage(image.id)} className="bg-red-600 hover:bg-red-700 text-xs px-2 py-1">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>)}
            </div>}

          {images.length === 0 && !loading && <div className="text-center text-gray-400 py-12">
              <ImageIcon className="w-12 h-12 mx-auto mb-4" />
              <p className="text-white">No images found for {selectedIndustryLabel}</p>
              <p className="text-sm text-gray-400 mt-2">Add some images to get started</p>
            </div>}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingImage} onOpenChange={() => setEditingImage(null)}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Edit {selectedIndustryLabel} Image</DialogTitle>
            <DialogDescription className="sr-only">Edit the title and description for this image</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-300 mb-2 block">Title</label>
              <Input value={formData.title} onChange={e => setFormData(prev => ({
              ...prev,
              title: e.target.value
            }))} className="bg-white/10 border-white/20 text-white" placeholder="Enter image title" />
            </div>
            
            <div>
              <label className="text-sm text-gray-300 mb-2 block">Description</label>
              <Textarea value={formData.description} onChange={e => setFormData(prev => ({
              ...prev,
              description: e.target.value
            }))} className="bg-white/10 border-white/20 text-white" placeholder="Enter image description" />
            </div>

            <div>
              <label className="text-sm text-gray-300 mb-2 block">Image Upload</label>
              <div className="space-y-2">
                <input type="file" accept="image/*" onChange={e => {
                const file = e.target.files?.[0];
                if (file) handleSingleFileUpload(file);
              }} className="hidden" id="image-upload-edit" />
                <label htmlFor="image-upload-edit" className="flex items-center justify-center w-full p-3 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/40 transition-colors">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Change Image'}
                </label>
                {formData.image_url && <div className="mt-2">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-32 object-cover rounded" 
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEzMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEzMCIgZmlsbD0iIzM3NDE0OSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5Q0E0QUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBQcmV2aWV3PC90ZXh0Pjwvc3ZnPg==';
                      }}
                    />
                  </div>}
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-300 mb-2 block">Display Order</label>
              <Input type="number" value={formData.display_order} onChange={e => setFormData(prev => ({
              ...prev,
              display_order: parseInt(e.target.value) || 1
            }))} className="bg-white/10 border-white/20 text-white" min="1" />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleUpdateImage} disabled={uploading || !formData.title || !formData.image_url} className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600">
                <Save className="w-4 h-4 mr-2" />
                Update Image
              </Button>
              <Button onClick={() => setEditingImage(null)} variant="outline" className="flex-1 border-white/20 text-white hover:bg-white/10">
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
};
export default AdminIndustryImages;